export interface StorefrontLocale {
  /** Shopper's active language as a lowercase ISO code, e.g. "fr". */
  language?: string;
  /** Shopper's country as an uppercase ISO code, e.g. "CA". */
  country?: string;
}

/**
 * Reads the shopper's active language and country, injected into
 * `window.redoStorefront.locale` by the theme app extension's env.liquid
 * (sourced from Shopify's Liquid `localization` object).
 *
 * Values are normalized — language lowercased, country uppercased — and blanks
 * (which Liquid renders when a value is unavailable) become `undefined`, so
 * callers can treat an absent locale as "no localization / show everywhere".
 */
export function getStorefrontLocale(): StorefrontLocale {
  const locale = globalThis.redoStorefront?.locale;
  return {
    language: normalizeCode(locale?.language)?.toLowerCase(),
    country: normalizeCode(locale?.country)?.toUpperCase(),
  };
}

export function getStorefrontMoneyLocale(): string | undefined {
  if (globalThis.redoStorefront?.type !== "shopify") {
    return undefined;
  }

  const { language, country } = getStorefrontLocale();
  const locale = language ?? globalThis.Shopify?.locale;
  const region = country ?? globalThis.Shopify?.country;
  return locale ? new Intl.Locale(locale, { region }).toString() : undefined;
}

/**
 * Base language code for copy localization, e.g. "fr". Prefers the injected
 * page locale — `navigator` is the shopper's browser and may not match it.
 */
export function getShopperLanguage(): string | undefined {
  const locale =
    getStorefrontLocale().language ??
    globalThis.Shopify?.locale ??
    globalThis.navigator?.language;
  return locale?.split("-")[0]?.toLowerCase();
}

// cspell:disable
const accessibilityTranslations = {
  en: {
    close: "Close",
    moreInformationAboutCoverage: "More information about coverage",
  },
  fr: {
    close: "Fermer",
    moreInformationAboutCoverage: "Plus d’informations sur la couverture",
  },
  es: {
    close: "Cerrar",
    moreInformationAboutCoverage: "Más información sobre la cobertura",
  },
  de: {
    close: "Schließen",
    moreInformationAboutCoverage: "Weitere Informationen zum Schutz",
  },
};
// cspell:enable

export function getAccessibilityTranslation() {
  const shopperLanguage = getShopperLanguage();
  return shopperLanguage && shopperLanguage in accessibilityTranslations
    ? accessibilityTranslations[
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        shopperLanguage as keyof typeof accessibilityTranslations
      ]
    : accessibilityTranslations.en;
}

function normalizeCode(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}
